import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, FileText, ImagePlus, Search, Trash2, Upload } from "lucide-react";
import { useDeferredValue, useRef, useState } from "react";
import { z } from "zod";
import { Button, IconButton, Input } from "@/components/ui/controls";
import { Dialog } from "@/components/ui/overlays";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/states";
import { toMediaAsset } from "@/lib/adapters/operations";
import { ApiError, apiRequest } from "@/lib/api/client";
import { mediaUploadDescription, mediaUploadResultSchema } from "@/lib/media-upload";
import { mediaSchema } from "@/lib/schemas/operations";
import styles from "./media-picker.module.css";
import { useOptionalToast } from "./ui/toast-context";

type MediaPickerProps = {
  assetId: string | null;
  imageUrl: string;
  onSelect: (assetId: string | null, imageUrl: string) => void;
  label?: string;
  kind?: "image" | "document";
  compact?: boolean;
  hidePreview?: boolean;
  previewFit?: "cover" | "contain";
  libraryOnly?: boolean;
  disabled?: boolean;
  allowedMimeTypes?: string[];
};

export function MediaPicker({
  assetId,
  imageUrl,
  onSelect,
  label = "Kapak görseli",
  kind = "image",
  compact = false,
  hidePreview = false,
  previewFit = "cover",
  libraryOnly = false,
  disabled = false,
  allowedMimeTypes,
}: MediaPickerProps) {
  const [open, setOpen] = useState(false);
  const notify = useOptionalToast();
  const uploadInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [changed, setChanged] = useState(false);
  const assets = useQuery({
    queryKey: ["media-picker", kind, deferredSearch],
    queryFn: async () =>
      (
        await apiRequest(
          `media/?kind=${kind}&page_size=100&search=${encodeURIComponent(deferredSearch)}`,
          z.array(mediaSchema),
        )
      ).data.map(toMediaAsset),
    enabled: open,
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file);
      body.append("locale", "tr");
      return (await apiRequest("media/", mediaUploadResultSchema, { method: "POST", body })).data;
    },
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: ["media"] });
      notify({
        title: asset.optimized ? "Görsel optimize edildi" : asset.deduplicated ? "Mevcut medya seçildi" : "Medya yüklendi",
        description: mediaUploadDescription(asset),
        tone: "success",
      });
      choose(asset.id, asset.url);
    },
    onError: (reason) => notify({
      title: "Medya yüklenemedi",
      description: reason instanceof ApiError ? reason.message : "Dosyayı kontrol edip tekrar deneyin.",
      tone: "danger",
    }),
  });

  function choose(id: string | null, url: string) {
    if (disabled) return;
    setChanged(true);
    onSelect(id, url);
    setOpen(false);
  }

  const libraryAssets = assets.data?.filter((asset) => !allowedMimeTypes || allowedMimeTypes.includes(asset.mimeType));

  return (
    <div className={`${styles.field} ${compact ? styles.compact : ""} ${hidePreview ? styles.noPreview : ""} ${previewFit === "contain" ? styles.contain : ""}`}>
      {libraryOnly ? <Button variant="secondary" disabled={disabled} onClick={() => setOpen(true)}>{kind === "image" ? <ImagePlus size={16} /> : <FileText size={16} />} Kütüphaneden seç</Button> : <>
      <span className={styles.label}>{label}</span>
      <input type="hidden" name="media_asset_id" value={assetId ?? ""} />
      <input
        type="hidden"
        name="media_asset_changed"
        value={changed ? "1" : "0"}
      />

      <div className={styles.value}>
        <div className={styles.currentPreview}>
          {imageUrl && kind === "image" ? (
            <img src={imageUrl} alt="Seçili medya" width={320} height={190} />
          ) : (
            <span aria-hidden="true">
              {kind === "image" ? <ImagePlus size={28} /> : <FileText size={28} />}
            </span>
          )}
        </div>
        <div className={styles.valueActions}>
          <div>
            <strong>{imageUrl ? "Medya seçildi" : "Henüz medya seçilmedi"}</strong>
            <span>
              {kind === "image"
                ? "Yeni bir görsel yükleyin veya kütüphaneden seçin."
                : "Yeni bir doküman yükleyin veya kütüphaneden seçin."}
            </span>
          </div>
          <div>
            <input
              ref={uploadInput}
              hidden
              type="file"
              disabled={disabled || upload.isPending}
              accept={kind === "image" ? "image/jpeg,image/png,image/webp,image/avif,image/gif,image/x-icon,image/vnd.microsoft.icon,image/svg+xml" : "application/pdf"}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) upload.mutate(file);
                event.currentTarget.value = "";
              }}
            />
            <Button disabled={disabled || upload.isPending} onClick={() => uploadInput.current?.click()}>
              <Upload size={16} /> {upload.isPending ? "Yükleniyor…" : compact ? "Dosya yükle" : "Yeni dosya yükle"}
            </Button>
            <Button variant="secondary" disabled={disabled} onClick={() => setOpen(true)}>
              <ImagePlus size={16} /> {compact ? "Kütüphane" : "Kütüphaneden seç"}
            </Button>
            {imageUrl ? (
              <IconButton
                label="Seçili medyayı kaldır"
                variant="danger"
                disabled={disabled}
                onClick={() => choose(null, "")}
              >
                <Trash2 size={16} />
              </IconButton>
            ) : null}
          </div>
        </div>
      </div>
      {upload.isError ? (
        <span className={styles.uploadError} role="alert">
          {upload.error instanceof ApiError ? upload.error.message : "Dosya yüklenemedi."}
        </span>
      ) : null}
      </>}

      <Dialog
        open={open}
        onOpenChange={(next) => { if (!next || !disabled) setOpen(next); }}
        title="Medya kütüphanesi"
        description={`İçerikte kullanacağınız ${kind === "image" ? "görseli" : "dokümanı"} seçin.`}
        size="lg"
      >
        <div className={`${styles.library} ${previewFit === "contain" ? styles.contain : ""}`}>
          <div className={styles.search}>
            <Search size={17} aria-hidden="true" />
            <Input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Dosya adına göre ara…"
              aria-label="Medya ara"
            />
          </div>

          {assets.isLoading ? (
            <Skeleton lines={6} label="Medya yükleniyor" />
          ) : assets.isError ? (
            <ErrorState
              title="Medya kütüphanesi yüklenemedi"
              description="Kayıtları görüntülemek için tekrar deneyin."
              onRetry={() => void assets.refetch()}
            />
          ) : libraryAssets?.length ? (
            <div className={styles.grid}>
              {libraryAssets.map((asset) => {
                const selected = asset.id === assetId;
                return (
                  <button
                    type="button"
                    disabled={disabled}
                    className={`${styles.card} ${selected ? styles.selected : ""}`}
                    onClick={() => choose(asset.id, asset.url)}
                    aria-pressed={selected}
                    key={asset.id}
                  >
                    <span className={styles.assetPreview}>
                      {kind === "image" ? (
                        <img
                          src={asset.url}
                          alt=""
                          width={480}
                          height={300}
                          loading="lazy"
                        />
                      ) : (
                        <FileText size={28} />
                      )}
                      {selected ? <span className={styles.selectedMark}><Check size={14} /></span> : null}
                    </span>
                    <span className={styles.assetBody}>
                      <strong>{asset.name}</strong>
                      <span>{asset.mimeType}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <EmptyState
              title="Uygun medya bulunamadı"
              description="Arama metnini değiştirin veya medya kütüphanesine yeni bir dosya yükleyin."
            />
          )}
        </div>
      </Dialog>
    </div>
  );
}
